import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/dist/query/react";

import * as devConfig from "../app/configs/devConfig";
import { getAuthHeader } from "../app/globalState/GlobalAuthState";
import {
  ClientInvoicePortalState,
  ClientPortalInvitee,
  ClientPortalInviteResult,
  ClientPortalOverview,
  ClientPortalShareResult,
} from "../features/clientPortal/clientPortal.types";

/** The firm's side of the client portal (backend api/client-portal). */
export const clientPortalSlice = createApi({
  reducerPath: "clientPortalSlice",
  tagTypes: ["ClientPortal", "ClientPortalInvoice"],
  baseQuery: fetchBaseQuery({
    baseUrl: `${devConfig.getServerBaseUrlForEnv()}`,
    prepareHeaders: (headers) => {
      const authHeader = getAuthHeader();
      headers.set("Authorization", authHeader.Authorization);
      return headers;
    },
  }),
  endpoints: (build) => ({
    getClientPortal: build.query<ClientPortalOverview, { customerId: string }>({
      query: ({ customerId }) => ({
        url: "api/client-portal/overview",
        params: { customerId },
      }),
      providesTags: ["ClientPortal"],
    }),
    inviteToPortal: build.mutation<
      ClientPortalInviteResult,
      { customerId: string; people: ClientPortalInvitee[]; message?: string }
    >({
      query: (body) => ({
        url: "api/client-portal/invite",
        method: "POST",
        body,
      }),
      invalidatesTags: ["ClientPortal"],
    }),
    resendPortalInvite: build.mutation<ClientPortalInviteResult, string>({
      query: (accessId) => ({
        url: `api/client-portal/access/${accessId}/resend`,
        method: "POST",
      }),
      invalidatesTags: ["ClientPortal"],
    }),
    removePortalAccess: build.mutation<boolean, string>({
      query: (accessId) => ({
        url: `api/client-portal/access/${accessId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["ClientPortal"],
    }),
    shareWithClient: build.mutation<
      ClientPortalShareResult,
      {
        documentIds: string[];
        shared: boolean;
        notify?: boolean;
        message?: string;
      }
    >({
      query: (body) => ({
        url: "api/client-portal/share",
        method: "PUT",
        body,
      }),
    }),
    uploadAndShare: build.mutation<
      ClientPortalShareResult,
      { customerId: string; files: File[]; notify: boolean; message?: string }
    >({
      query: ({ customerId, files, notify, message }) => {
        const body = new FormData();
        files.forEach((f) => body.append("files", f));
        body.append("notify", String(notify));
        if (message) body.append("message", message);
        return {
          url: `api/client-portal/clients/${customerId}/upload-shared`,
          method: "POST",
          body,
        };
      },
    }),
    getInvoicePortalState: build.query<ClientInvoicePortalState, string>({
      query: (invoiceId) => `api/client-portal/invoices/${invoiceId}`,
      providesTags: ["ClientPortalInvoice"],
    }),
    shareInvoice: build.mutation<
      ClientInvoicePortalState,
      { invoiceId: string; shared: boolean; notify?: boolean; message?: string }
    >({
      query: ({ invoiceId, ...body }) => ({
        url: `api/client-portal/invoices/${invoiceId}/share`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["ClientPortalInvoice"],
    }),
    markClientUploadsSeen: build.mutation<number, string>({
      query: (customerId) => ({
        url: `api/client-portal/clients/${customerId}/seen`,
        method: "PUT",
      }),
    }),
  }),
});

export const {
  useGetClientPortalQuery,
  useInviteToPortalMutation,
  useResendPortalInviteMutation,
  useRemovePortalAccessMutation,
  useShareWithClientMutation,
  useUploadAndShareMutation,
  useMarkClientUploadsSeenMutation,
  useGetInvoicePortalStateQuery,
  useShareInvoiceMutation,
} = clientPortalSlice;
